import React, { Suspense, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  ContractType,
  FileAgreement,
  Show,
} from "@quantizr-engineering/common/types/show";
import { useFileService } from "../../api/fileService";
import { useShowService, useShowOfferService } from "../../api/showService";
import { Breadcrumbs } from "../../components/Breadcrumbs";
import { Field, FieldInputProps } from "formik";
import ReusableFormikWrapper from "../../contexts/ReusableFormikWrapper";
import { FileStatus } from "@quantizr-engineering/common/types/file";
import { getDocumentStatusStyling } from "../../utils/documentStatus";
import SummaryTabAccordion from "../../components/Documents/DocumentAnalysisView/SummaryTabAccordion";
import { stringLengthLimiter } from "../../utils/utils.ts";
import DataGroup from "../../components/Documents/DocumentAnalysisView/DataGroup.tsx";
import { DocMap, documentFieldMaps, FieldsMap } from "../../types/formMaps";
import { DataPoint } from "../../components/Documents/DocumentAnalysisView/DataPoint.tsx";
import { useBrandDealService } from "../../api/brandDealService.ts";
import Tooltip from "../../components/Tooltip.tsx";
import { useCreedInsuranceContractService } from "../../api/insuranceContractService";
import { useCreedRecordLabelAgreementService } from "../../api/recordLabelService";

const PdfViewer = React.lazy(() => import("../../components/PdfViewer"));

const TABS = [
  { id: "summary", label: "Summary" },
  { id: "data", label: "Data" },
  { id: "related", label: "Related" },
];

const DocumentDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedData, setSelectedData] = useState<any>(null);
  const [selectedDoc, setSelectedDoc] = useState<FileAgreement | null>(null);
  const [selectedDocMap, setSelectedDocMap] = useState<DocMap | null>(null);
  const { shows } = useShowService();
  const { creedShowOffers } = useShowOfferService();
  const { brandDeals } = useBrandDealService();
  const { creedInsuranceContracts } = useCreedInsuranceContractService();
  const { creedRecordLabelAgreements } = useCreedRecordLabelAgreementService();
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const { fileEntities, fetchPdfContent } = useFileService();
  const [activeTab, setActiveTab] =
    useState<(typeof TABS)[number]["id"]>("summary");

  useEffect(() => {
    if (!id) {
      setError("Document ID is missing.");
      setLoading(false);
      return;
    }

    const documentId = parseInt(id);
    const doc =
      (fileEntities && fileEntities.find((doc) => doc.id === documentId)) ||
      null;

    setSelectedDoc(doc);

    if (!doc) return;

    setSelectedDocMap(documentFieldMaps[doc.contractType]);

    switch (doc.contractType) {
      case ContractType.SHOW_OFFER:
        const creedShowOffer = creedShowOffers && creedShowOffers.find((creed) => creed.id === doc.creedShowOffer.id);
        setSelectedData(creedShowOffer);
        break;
      case ContractType.LIVE_MUSIC_AGREEMENT:
      case ContractType.LIVE_MUSIC_SETTLEMENT:
        const show =
          (shows &&
            shows.find((show) => doc?.shows.some((ds) => ds.id === show.id))) ||
          null;
        setSelectedData(show);
        break;
      case ContractType.BRAND_DEAL:
        const brandDeal =
          brandDeals && brandDeals.find((bd) => bd.id === doc.brandDeal);
        setSelectedData(brandDeal);
        break;
      case ContractType.INSURANCE_CONTRACT:
        const creedInsuranceContract =
          creedInsuranceContracts &&
          creedInsuranceContracts.find(
            (creed) => creed.id === doc.creedInsuranceContract.id
          );
        setSelectedData(creedInsuranceContract);
        break;
      case ContractType.RECORDING_RECORD_LABEL_AGREEMENT:
        const creedRecordLabelAgreement =
          creedRecordLabelAgreements &&
          creedRecordLabelAgreements.find(
            (creed) => creed.id === doc.creedRecordLabelAgreement.id
          );
        setSelectedData(creedRecordLabelAgreement);
        break;
      default:
        setError("Invalid contract type.");
        setLoading(false);
        return;
    }
  }, [id, shows, fileEntities, brandDeals]);

  useEffect(() => {
    if (id && selectedDoc) {
      const fetchPdf = async () => {
        try {
          const arrayBuffer = await fetchPdfContent(selectedDoc.id as number);
          const blob = new Blob([arrayBuffer], { type: "application/pdf" });
          const url = URL.createObjectURL(blob);

          setPdfUrl(url);
          setLoading(false);
        } catch (error) {
          setError("Error fetching PDF content.");
          console.error("Error fetching PDF content:", error);
          setLoading(false);
        }
      };
      fetchPdf();
    }
  }, [id, selectedDoc, fetchPdfContent]);

  if (!selectedDoc)
    return error ? (
      <div>Error loading document {error}</div>
    ) : (
      <div>Loading...</div>
    );

  return (
    <div className="flex w-full h-[calc(100vh-60px)]">
      {/* Left side - PDF Viewer (50% width) */}
      <div className="w-1/2 p-4">
        <div className="h-full rounded-lg">
          <Suspense
            fallback={
              <div className="w-full flex items-center justify-center">
                Loading PDF viewer...
              </div>
            }
          >
            <PdfViewer pdfUrl={pdfUrl} loading={loading} />
          </Suspense>
        </div>
      </div>
      {/* Right side - Document details (50% width) */}
      {!!selectedData && (
        <div className="w-1/2 p-4 flex flex-col">
          <div className="bg-white rounded-lg flex flex-col h-full">
            <ReusableFormikWrapper
              data={{ doc: selectedDoc || {}, data: selectedData || {} }}
            >
              {({ values }) => (
                <div className="flex flex-col h-full">
                  <div className="px-4 pt-2 flex-shrink-0">
                    <Breadcrumbs
                      items={[
                        { text: "Documents", link: "/documents" },
                        {
                          text: selectedDoc?.name
                            ? stringLengthLimiter(selectedDoc.name, 44)
                            : "Document Details",
                        },
                      ]}
                    />
                  </div>
                  <div className="flex flex-col w-full px-4 shadow-sm py-2 flex-shrink-0">
                    <Field name="doc.name">
                      {({ field }: { field: FieldInputProps<string> }) => (
                        <div className="relative group">
                          <Tooltip content={field.value}>
                            <div className="text-2xl font-bold text-black max-w-[85%] truncate">
                              {field.value || "Document Title"}
                            </div>
                          </Tooltip>
                        </div>
                      )}
                    </Field>
                    <div className="flex flex-wrap items-center gap-2 mt-2">
                      <div className="text-sm text-[#48455B]">
                        {selectedDoc?.contractType || "Document Type"}
                      </div>
                      <div
                        className={`px-3 py-1 rounded-full text-xs ${getDocumentStatusStyling(
                          selectedDoc.status || FileStatus.PENDING
                        )}`}
                      >
                        {selectedDoc.status || "Unknown"}
                      </div>
                    </div>
                  </div>

                  {/* Tabs and Content Area */}
                  <div className="flex flex-col flex-grow overflow-hidden rounded-lg">
                    <div className="flex border-b border-gray-200 flex-shrink-0">
                      {TABS.map((tab) => (
                        <button
                          type="button"
                          key={tab.id}
                          onClick={() => setActiveTab(tab.id)}
                          className={`
                    p-4
                    text-[#48455B]
                    border-b-2
                    transition-colors
                    duration-200
                    hover:text-[#6D53FD]
                    ${
                      activeTab === tab.id
                        ? "border-[#6D53FD] text-[#6D53FD]"
                        : "border-transparent"
                    }
                  `}
                        >
                          {tab.label}
                        </button>
                      ))}
                    </div>

                    <div className="overflow-y-auto flex-grow pt-4">
                      {activeTab === "summary" && (
                        <div className="h-full">
                          {Object.entries(
                            selectedDocMap.summaryFields as FieldsMap
                          ).map(([key, fieldDef], index) => (
                            <SummaryTabAccordion
                              key={index}
                              title={fieldDef.label}
                              items={fieldDef.subfields ?? []}
                              values={values}
                            />
                          ))}
                        </div>
                      )}
                      {activeTab === "data" && (
                        <div className="space-y-4">
                          {selectedDoc?.contractType &&
                            Object.entries(
                              selectedDocMap.dataFields as FieldsMap
                            ).map(([key, fieldDef]) => {
                              // Only render DataGroup for fields that have subfields (typically array types)
                              if (
                                !!fieldDef.subfields &&
                                fieldDef.subfields.length > 0
                              ) {
                                return (
                                  <DataGroup
                                    key={key}
                                    fieldDef={fieldDef}
                                    values={values.data}
                                  />
                                );
                              }
                              if (!fieldDef.subfields) {
                                return (
                                  <DataPoint
                                    key={key}
                                    fieldDef={fieldDef}
                                    values={values.data}
                                  />
                                );
                              }
                              return null;
                            })}
                        </div>
                      )}
                      {activeTab === "related" &&
                        !!selectedDocMap?.entityHref &&
                        !!selectedData && (
                          <a
                            href={`${selectedDocMap.entityHref(selectedData)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-500"
                          >
                            <div className="flex items-center space-x-2 w-50% p-4 m-4 rounded-lg hover:bg-gray-50 cursor-pointer">
                              <div className="text-gray-500 flex items-center">
                                <img src={selectedDocMap.entityHrefIcon()} />
                              </div>
                              <div>
                                <div className="font-bold text-black">
                                  {selectedDocMap.entityHrefTitle(selectedData)}
                                </div>
                                <div className="text-gray-700">
                                  {selectedDocMap.entityHrefSubtitle(
                                    selectedData
                                  )}
                                </div>
                              </div>
                            </div>
                          </a>
                        )}
                      {activeTab === "related" &&
                        selectedDocMap &&
                        !selectedDocMap.entityHref && (
                          <div className="flex items-start space-x-2 w-50% p-4 m-4 rounded-lg hover:bg-gray-50">
                            <div className="text-gray-500">
                              <img src={selectedDocMap.entityHrefIcon()} />
                            </div>
                            <div>
                              <div className="font-bold text-black">
                                {selectedDocMap.entityHrefTitle(selectedData)}
                              </div>
                              <div className="text-gray-700">
                                {selectedDocMap.entityHrefSubtitle(
                                  selectedData
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                    </div>
                  </div>
                </div>
              )}
            </ReusableFormikWrapper>
          </div>
        </div>
      )}
      {!selectedData && (
        <div className="flex justify-center items-center h-screen w-1/2">
          <div className="text-center mb-32">
            <h1 className="text-4xl text-center font-bold text-gray-800 ">
              No Data Found For This Document
            </h1>
            Contact support for assistance.
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentDetails;
